/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { NewCoreIconProps } from '@atlaskit/icon';
import EditIcon from '@atlaskit/icon/core/edit';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationBlockIcon from '@atlaskit/icon/core/quotation-block';
import TextIcon from '@atlaskit/icon/core/text';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
} from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
	type ResolvedExperienceMeta,
} from '@atlassian/native-embeds-core/types';

export const PAGE_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('page');

const pageIcons = [PageIcon, EditIcon, TextIcon, QuotationBlockIcon];

const isPageUrl = (url: string): boolean =>
	// Match page URLs: /wiki/spaces/<key>/pages/<id>
	// Match blog URLs: /wiki/spaces/<key>/blog/<id> or /wiki/spaces/<key>/blog/<year>/<month>/<day>/<id>
	/\/wiki\/spaces\/[^/]+\/(pages\/\d+|blog\/(\d+\/){0,3}\d+)/.test(url);

const isEditUrl = (url: string): boolean =>
	/\/wiki\/spaces\/[^/]+\/(pages|blog)\/(edit-embed|edit-v2)\/\d+/.test(url);

/**
 * Experience manifest for a page embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(PageExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const PageExperienceManifest: {
	readonly enforceQueryParams: true;
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly shouldFocusOnClick: (url: string, meta: ResolvedExperienceMeta) => boolean;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: Record<string, never>;
		readonly headerControls: {
			readonly editButton: {
				readonly onSwitchToEditing: (currentUrl: string) => string;
				readonly onSwitchToViewing: (currentUrl: string) => string;
				readonly show: (meta: ResolvedExperienceMeta) => boolean;
			};
		};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showHeader: (_meta: ResolvedExperienceMeta, url: string) => boolean;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
	};
} = {
	enforceQueryParams: true,
	id: PAGE_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
	isUrlMatch: isPageUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	isActive: (): true => true,
	shouldFocusOnClick: (url: string, meta: ResolvedExperienceMeta): boolean => {
		// Don't steal focus for live docs (documentType === 'schema:DigitalDocument')
		if (meta.documentType === 'schema:DigitalDocument') {
			return false;
		}
		// Don't steal focus when the page is in edit mode
		if (isEditUrl(url)) {
			return false;
		}
		return true;
	},
	transformUrl: (url: string, _isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		urlObj.searchParams.set('sample-param', 'true');
		return urlObj.toString();
	},
	uiConfig: {
		showHeader: (_meta: ResolvedExperienceMeta, url: string): boolean => !isEditUrl(url),
		headerControls: {
			editButton: {
				show: (meta: ResolvedExperienceMeta): boolean =>
					meta.documentType !== 'schema:DigitalDocument', // Don't show edit button for live docs
				onSwitchToEditing: (currentUrl: string, originalUrl?: string): string => {
					const url = new URL(currentUrl);
					// Detect whether this is originally a blog URL. The iframe's internal
					// SPA router may resolve blog URLs (e.g. /blog/2026/04/29/<id>) to
					// their canonical /pages/<id> form, so iframeSrc (currentUrl) may
					// show /pages/<id> even for blogs. We check the originalUrl (the URL
					// before iframe navigation) to detect blogs reliably.
					const isBlogUrl = originalUrl
						? /\/wiki\/spaces\/[^/]+\/blog\//.test(new URL(originalUrl).pathname)
						: /\/wiki\/spaces\/[^/]+\/blog\//.test(url.pathname);

					if (isBlogUrl) {
						// Blogs use /blog/edit-v2/<id> because there is no /blog/edit-embed/ route,
						// which would cause a redirect that strips native embed query params.
						// Extract the content ID from either the blog URL or the resolved /pages/<id> URL.
						const blogMatch = url.pathname.match(/\/blog\/(?:\d+\/\d+\/\d+\/)?(\d+)(?:\/[^/]*)?$/);
						const pagesMatch = url.pathname.match(/\/pages\/(\d+)/);
						const contentId = blogMatch?.[1] ?? pagesMatch?.[1];
						if (contentId) {
							// Replace the path portion after /spaces/<key>/ with /blog/edit-v2/<id>
							url.pathname = url.pathname.replace(
								/\/spaces\/([^/]+)\/.*$/,
								`/spaces/$1/blog/edit-v2/${contentId}`,
							);
						}
					} else {
						// Pages use /pages/edit-embed/<id> (dedicated embedded editor route).
						url.pathname = url.pathname.replace(/\/pages\/(\d+)/, '/pages/edit-embed/$1');
					}
					url.searchParams.set('initialAllowedFeatures', 'delete-draft');
					return url.toString();
				},
				onSwitchToViewing: (currentUrl: string, _originalUrl?: string): string => {
					const url = new URL(currentUrl);
					// Reverse the edit URL transforms back to view URLs
					url.pathname = url.pathname
						.replace(/\/pages\/edit-embed\/(\d+)/, '/pages/$1')
						.replace(/\/blog\/edit-v2\/(\d+)/, '/blog/$1');
					url.searchParams.delete('initialAllowedFeatures');
					return url.toString();
				},
			},
		},
		editorToolbarActions: createEditorToolbarActions({
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				BUILTIN_TOOLBAR_KEYS.SUMMARISE,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.EDIT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={pageIcons}
				testId={props.testId}
			/>
		),
		titleIcon: PageIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
